import React, { useState, FC, useEffect, useMemo } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, RowData, SortingState, useReactTable} from '@tanstack/react-table';
import {  Row, Col, Pagination, Form, Spinner } from 'react-bootstrap';
import { SimulationResultRow } from '../core/_models';
import { formatNumber } from "../../../../libs/misc";
import clsx from 'clsx';


declare module '@tanstack/react-table' { 
  interface ColumnMeta<TData extends RowData, TValue> { 
    headerClass: string; 
    cellClass:string
  }
}

const columnHelper = createColumnHelper<SimulationResultRow>()

interface InputOutputResultTableProps {
  resultData: any
}

const InputOutputResultTable: FC<InputOutputResultTableProps> = ({ resultData }) => {
  const [simulationResult, setSimulationResult] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true)
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })

  const putZeroLastSortFn = (rowA, rowB, columnId) => {
    const valueA = +rowA.original[columnId] || 0
    const valueB = +rowB.original[columnId] || 0
    // Find current sorting direction for the column
    const sortDir = sorting.find((sort) => {
      return sort.id === columnId
    })?.desc;
    if (sortDir) {
      // Descending order: Place null at the bottom
      if (valueA === 0) return -1; // impactA is 'N/A', move it down
      if (valueB === 0) return 1; // impactB is 'N/A', move it down
    } else {
      // Ascending order: Place null at the bottom
      if (valueA === 0) return 1; // impactA is 'N/A', move it down
      if (valueB === 0) return -1; // impactB is 'N/A', move it down
    }
    return valueA - valueB; // Ascending order for valid values
  }

  const columns = [
    columnHelper.accessor('oecd_code', {
      header: 'ISIC Rev.4',
      cell: info => {
        let hsCodes;
        if(info.row.original.hs_code_concordance && info.row.original.hs_code_concordance.length>0 ){
          hsCodes = info.row.original.hs_code_concordance
        }
        return (
          <span className="badge text-white bg-dark w-50px d-inline-block" title={hsCodes}>
            {info.getValue()}
          </span>
        )
      },
      meta: {
        headerClass: 'text-center',
        cellClass: 'text-center'
      }
    }),
    columnHelper.accessor('desc', {
      header: 'Sector Description',
      cell: info => (
        <small>
          {info.getValue()}
        </small>
      ),
      meta: {
        headerClass: 'w-200px',
        cellClass: 'w-200px text-left'
      }
    }),
    columnHelper.accessor('domestic_export_change', {
      header: `Total Export Changes (US$ Millions)`,
      cell: info => formatNumber((info.getValue() ?? 0) / 1000000),
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end'
      },
      sortingFn:putZeroLastSortFn
    }),
    columnHelper.accessor('demand_original', {
      header: `Original Demand (US$ Millions)`,
      cell: info => formatNumber(info.getValue()),
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end'
      },
      sortingFn:putZeroLastSortFn
    }),
    columnHelper.accessor('demand_new', {
      header: `New Demand (US$ Millions)`,
      cell: info => formatNumber(info.getValue()),
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end'
      },
      sortingFn:putZeroLastSortFn
    }),
    columnHelper.accessor('output_original', {
      header: `Original Output (US$ Millions)`,
      cell: info => formatNumber(info.getValue()),
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end'
      },
      sortingFn:putZeroLastSortFn
    }),
    columnHelper.accessor('output_new', {
      header: `New Output (US$ Millions)`,
      cell: info => formatNumber(info.getValue()),
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end'
      },
      sortingFn:putZeroLastSortFn
    }),
    columnHelper.accessor('delta_output', {
      header: `Output Change (US$ Millions)`,
      cell: info => {
        const rawValue = info.getValue();
        if(rawValue &&  (+rawValue===-0 || +rawValue===0)){
          return 0
        }
        return formatNumber(rawValue)
        // else if(rawValue && +rawValue > 0) {
        //   return (<span className="text-success fw-bolder">+{formatNumber(rawValue)}</span>);
        // }
        // return (<span className="text-danger fw-bolder">{formatNumber(rawValue)}</span>);
      },
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end'
      },
      sortingFn:putZeroLastSortFn
    }),
    columnHelper.accessor('delta_output_percentage', {
      header: `Output Change (%)`,
      cell: info => {
        const rawValue = info.getValue();
        if(rawValue && (+rawValue===-0 || rawValue===0)){
          return '0%'
        }else if(rawValue && +rawValue > 0) {
          return (<span className="text-success fw-bolder">+{formatNumber(rawValue)}%</span>);
        }
        return (<span className="text-danger fw-bolder">{formatNumber(rawValue)}%</span>);
      },
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end'
      },
      sortingFn:putZeroLastSortFn
    }),
  ]
  
  // [Option 1] To make sure the data have a stable reference for React-Table
  const resultRowsMemo = useMemo(()=>simulationResult || [], [simulationResult])

  const formDataTable = useReactTable({
      data:simulationResult || [],
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      onGlobalFilterChange: setGlobalFilter,
      onSortingChange: setSorting,
      state: {
        pagination,
        globalFilter,
        sorting
      },
      // [Option 2] To make sure the data have a stable reference for React-Table
      autoResetPageIndex: false,
      autoResetExpanded: false,
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1;
    const totalPages = formDataTable.getPageCount();
    const maxPagesToShow = 3;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range:any = [];

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }

    return range;
  }

  const handlePageChange = (page) => {
    const currentPage = pagination.pageIndex+1
    const totalPages = formDataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      formDataTable.setPageIndex(page-1)
    }
  };

  const handleDisplayPageChange = (e) =>{
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value
    })
  }

  const pages = paginationRange();
  const totalCount = formDataTable.getRowCount()
  const firstIndexPerPages = ((formDataTable.getState().pagination.pageIndex) * formDataTable.getState().pagination.pageSize) + 1
  const lastIndexPerPages = ((formDataTable.getState().pagination.pageIndex) * formDataTable.getState().pagination.pageSize) + formDataTable.getRowModel().rows.length
  const displayHeader = formDataTable.getHeaderGroups().map(headerGroup=>(
    <tr className='fw-normal py-4 text-center align-middle' key={headerGroup.id}>
    {
      headerGroup.headers.map(header=>{
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div 
              className={header.column.getCanSort()
                  ? 'cursor-pointer select-none'
                  : ''
              }
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                      ? 'Sort descending'
                      : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              <span className="ms-2"> 
                {{
                  asc: (<i className="fa-solid fa-sort-up"></i>),
                  desc: (<i className="fa-solid fa-sort-down"></i>),
                  false: (<i className="fa-solid fa-sort"></i>),
                }[header.column.getIsSorted() as string] ?? null}
              </span>
              </div>
          </th>
        )
      })
    }
    </tr>
  ))
  const displayBody = formDataTable.getRowModel().rows.map(row=>(
    <tr key={row.id} className='align-middle'>
      {
        row.getVisibleCells().map(cell=>{
          return (
            <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
              {flexRender(
                cell.column.columnDef.cell, 
                cell.getContext())
              }
            </td>
          )
        })
      }
    </tr>
  ))
  
  // Cleanup function to reset the state when the component is unmounted 
  useEffect(()=>{
    setSimulationResult([])
    setIsLoading(true)
    return () => {
      setSimulationResult([])
      setIsLoading(true)
    };
  },[])

  useEffect(() => {
    setSimulationResult(resultData);
    setIsLoading(false)
  }, [resultData]);

  return (
    <>
      {!isLoading && (simulationResult?.length??0)>0 ? (
        <>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              <i className="fa-solid fa-circle text-success fs-9 me-2"></i>Showing {firstIndexPerPages} to {lastIndexPerPages} of {totalCount} simulations results
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              {/* <Form.Group as={Row} className="mb-6" controlId="">
                <Form.Label className="text-end text-primary me-0" column>
                  <i className="fa-solid fa-magnifying-glass fa-rotate-90 text-primary fs-9 me-2"></i>Search :
                </Form.Label>
                <Col md={6} className="d-flex align-items-center">
                  <Form.Control
                    type="text"
                    className="form-control-sm"
                    onChange={e => formDataTable.setGlobalFilter(String(e.target.value))} />
                  </Col>
              </Form.Group> */}
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="table-responsive">
                <table className="gy-3 table table-striped table-bordered table-hover" id="impact_result_table">
                  <thead className="table-dark">
                    {displayHeader}
                  </thead>    
                  <tbody className="gy-1 overflow-auto">
                    {displayBody}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
          {/* <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size="sm"
                className="w-80px mx-2"
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}>
                <option value="10">10</option>
                <option value="50">50</option>
              </Form.Select>
              simulation result 
            </Col> 
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => formDataTable.previousPage()}
                  disabled={!formDataTable.getCanPreviousPage()} />
                {pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <Pagination.Ellipsis key={index} />
                    );
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  onClick={() => formDataTable.nextPage()}
                  disabled={!formDataTable.getCanNextPage()} />
              </Pagination>
            </Col>
          </Row> */}
        </>
      ):(
        <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
          <Spinner animation="border" role="status" variant="primary" className="me-3" />
          <div className="loading-text">
            Loading
            <span className="dot">.</span>
            <span className="dot">.</span>
            <span className="dot">.</span>
          </div>
        </div>
      )}
    </>
  )
}

export default InputOutputResultTable